import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    apiGetLead, apiDeleteLead,
    apiCreateFollowup, apiUpdateFollowup, apiDeleteFollowup,
} from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import CalendarPicker from '../components/CalendarPicker';

const fmtDate  = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const fmtDateT = (d) => d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

const fuEmpty = { followup_date: '', notes: '', status: 'Pending' };

export default function LeadDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();

    const [lead, setLead] = useState(null);
    const [followups, setFollowups] = useState([]);
    const [timeline, setTimeline] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState('info');

    const [fuForm, setFuForm] = useState(fuEmpty);
    const [fuEdit, setFuEdit] = useState(null); // id being edited
    const [fuSaving, setFuSaving] = useState(false);
    const [showFuForm, setShowFuForm] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiGetLead(id);
            const { followups: fu, timeline: tl, ...l } = res.data.data;
            setLead(l);
            setFollowups(fu || []);
            setTimeline(tl || []);
        } catch {
            toast.error('Lead not found');
            navigate('/leads');
        } finally {
            setLoading(false);
        }
    }, [id, navigate]);

    useEffect(() => { load(); }, [load]);

    const handleDelete = async () => {
        if (!window.confirm('Permanently delete this lead?')) return;
        try {
            await apiDeleteLead(id);
            toast.success('Lead deleted');
            navigate('/leads');
        } catch (e) {
            toast.error(e.response?.data?.message || 'Delete failed');
        }
    };

    const handleFuChange = (e) => setFuForm(f => ({ ...f, [e.target.name]: e.target.value }));

    const handleFuSubmit = async (e) => {
        e.preventDefault();
        if (!fuForm.followup_date) return toast.error('Please set a follow-up date');
        setFuSaving(true);
        try {
            if (fuEdit) {
                await apiUpdateFollowup(fuEdit, fuForm);
                toast.success('Follow-up updated');
            } else {
                await apiCreateFollowup({ ...fuForm, lead_id: id });
                toast.success('Follow-up scheduled');
            }
            setFuForm(fuEmpty);
            setFuEdit(null);
            setShowFuForm(false);
            load();
        } catch (e) {
            toast.error(e.response?.data?.message || 'Save failed');
        } finally {
            setFuSaving(false);
        }
    };

    const handleFuEdit = (fu) => {
        setFuEdit(fu.id);
        setFuForm({ followup_date: fu.followup_date?.split('T')[0] || '', notes: fu.notes || '', status: fu.status });
        setShowFuForm(true);
        setTab('followups');
    };

    const handleFuDelete = async (fuId) => {
        if (!window.confirm('Delete this follow-up?')) return;
        try {
            await apiDeleteFollowup(fuId);
            toast.success('Follow-up removed');
            load();
        } catch {
            toast.error('Delete failed');
        }
    };

    if (loading) return <div className="loading-center" style={{ minHeight: '80vh' }}><div className="spinner" /><span>Loading…</span></div>;
    if (!lead) return null;

    const statusColor = {
        'New': 'blue', 'Contacted': 'purple', 'Follow-up': 'orange',
        'Will Visit': 'teal', 'Visited': 'cyan',
        'Converted': 'green', 'Not Interested': 'red',
    }[lead.status] || 'blue';

    return (
        <div className="page">
            {/* Header */}
            <div className="page-header">
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                        <h1 className="page-title">{lead.name}</h1>
                        <span className={`badge badge--${lead.status.replace(/\s+/g, '-')}`}>{lead.status}</span>
                    </div>
                    <p className="page-subtitle">📞 {lead.phone}{lead.email ? ` · ✉️ ${lead.email}` : ''}</p>
                </div>
                <div className="page-actions">
                    <button className="btn btn-ghost" onClick={() => navigate('/leads')}>← Back</button>
                    <button id="edit-lead-btn" className="btn btn-ghost" onClick={() => navigate(`/leads/${id}/edit`)}>✏️ Edit</button>
                    {user?.role === 'admin' && (
                        <button id="delete-lead-btn" className="btn btn-danger" onClick={handleDelete}>🗑 Delete</button>
                    )}
                </div>
            </div>

            {/* Tabs */}
            <div className="tabs">
                {[
                    { key: 'info', label: '📋 Details' },
                    { key: 'followups', label: `📅 Follow-ups (${followups.length})` },
                    { key: 'timeline', label: `🕐 Timeline (${timeline.length})` },
                ].map(t => (
                    <button key={t.key} className={`tab-btn${tab === t.key ? ' active' : ''}`} onClick={() => setTab(t.key)}>
                        {t.label}
                    </button>
                ))}
            </div>

            {/* Details Tab */}
            {tab === 'info' && (
                <div className="card">
                    <div className="card-body">
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '24px' }}>
                            {[
                                { label: 'Course', value: lead.course },
                                { label: 'Source', value: lead.source },
                                { label: 'Status', value: <span className={`badge badge--${lead.status.replace(/\s+/g, '-')}`}>{lead.status}</span> },
                                { label: 'Assigned Counselor', value: lead.counselor_name || 'Unassigned' },
                                { label: 'Created On', value: fmtDate(lead.created_at) },
                                { label: 'Last Updated', value: fmtDate(lead.updated_at) },
                            ].map(({ label, value }) => (
                                <div key={label}>
                                    <div style={{ fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', marginBottom: '4px' }}>{label}</div>
                                    <div style={{ fontSize: '0.9rem', color: 'var(--text-primary)' }}>{value || '—'}</div>
                                </div>
                            ))}
                        </div>

                        {lead.notes && (
                            <div style={{ marginTop: '28px', paddingTop: '20px', borderTop: '1px solid var(--border)' }}>
                                <div style={{ fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', marginBottom: '8px' }}>Notes</div>
                                <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', lineHeight: 1.7 }}>{lead.notes}</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Follow-ups Tab */}
            {tab === 'followups' && (
                <div className="card">
                    <div className="card-header">
                        <span className="card-title">Follow-up Schedule</span>
                        <button
                            id="add-followup-btn"
                            className="btn btn-primary btn-sm"
                            onClick={() => { setFuForm(fuEmpty); setFuEdit(null); setShowFuForm(s => !s); }}
                        >
                            ➕ Add Follow-up
                        </button>
                    </div>

                    {showFuForm && (
                        <div style={{ padding: '20px 22px', borderBottom: '1px solid var(--border)', background: 'var(--bg-elevated)' }}>
                            <form id="followup-form" onSubmit={handleFuSubmit}>
                                <div className="form-row" style={{ gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                    {/* ── Calendar Date Picker ── */}
                                    <div className="form-group" style={{ marginBottom: 0 }}>
                                        <label>Date *</label>
                                        <CalendarPicker
                                            id="fu-date"
                                            value={fuForm.followup_date}
                                            onChange={(dateStr) => setFuForm(f => ({ ...f, followup_date: dateStr }))}
                                            placeholder="📅 Pick a follow-up date"
                                        />
                                    </div>
                                    <div className="form-group" style={{ marginBottom: 0 }}>
                                        <label htmlFor="fu-status">Status</label>
                                        <select id="fu-status" name="status" value={fuForm.status} onChange={handleFuChange}>
                                            <option>Pending</option>
                                            <option>Done</option>
                                            <option>Rescheduled</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="form-group" style={{ marginTop: '14px', marginBottom: 0 }}>
                                    <label htmlFor="fu-notes">Notes</label>
                                    <input id="fu-notes" name="notes" value={fuForm.notes} onChange={handleFuChange} placeholder="Optional notes about this follow-up…" />
                                </div>
                                <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                                    <button type="submit" id="fu-save-btn" className="btn btn-primary btn-sm" disabled={fuSaving}>
                                        {fuSaving ? '…' : fuEdit ? '✔ Update Follow-up' : '✔ Schedule Follow-up'}
                                    </button>
                                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setShowFuForm(false); setFuEdit(null); }}>Cancel</button>
                                </div>
                            </form>
                        </div>
                    )}

                    {followups.length === 0 ? (
                        <div className="empty-state"><div className="empty-state-icon">📅</div><p>No follow-ups scheduled yet.</p></div>
                    ) : (
                        <div className="table-wrapper">
                            <table className="data-table">
                                <thead>
                                    <tr><th>Date</th><th>Status</th><th>Notes</th><th>Actions</th></tr>
                                </thead>
                                <tbody>
                                    {followups.map(fu => (
                                        <tr key={fu.id} id={`followup-row-${fu.id}`}>
                                            <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(fu.followup_date)}</td>
                                            <td><span className={`badge badge--${fu.status}`}>{fu.status}</span></td>
                                            <td>{fu.notes || '—'}</td>
                                            <td>
                                                <div className="action-cell">
                                                    <button className="btn btn-ghost btn-sm btn-icon" title="Edit" onClick={() => handleFuEdit(fu)}>✏️</button>
                                                    <button className="btn btn-danger btn-sm btn-icon" title="Delete" onClick={() => handleFuDelete(fu.id)}>🗑</button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* Timeline Tab */}
            {tab === 'timeline' && (
                <div className="card">
                    <div className="card-header"><span className="card-title">Activity Timeline</span></div>
                    {timeline.length === 0 ? (
                        <div className="empty-state"><div className="empty-state-icon">🕐</div><p>No activity recorded yet.</p></div>
                    ) : (
                        <div className="card-body">
                            <ul className="timeline" id="lead-timeline">
                                {[...timeline].reverse().map(t => (
                                    <li key={t.id} className="timeline-item">
                                        <div className="timeline-action">{t.action}</div>
                                        <div className="timeline-meta">
                                            {t.user_name && <span>{t.user_name} · </span>}
                                            {fmtDateT(t.created_at)}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
